"use client";

import React from "react";
import Link from "next/link";
import { Check } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/cards/card/card";
import { ContentCard } from "@/types/cms";
import { renderPriceBody, renderValuePropsMarkdown } from "@/lib/richText";
import { Button } from "@/components/button/button/button";
import { Badge } from "@/components/badge/badge";
import CardOverlay from "@/components/cards/cardOverlay/cardOverlay";
import {
  normalizeCardButtons,
  splitButtons,
  type ButtonVisibility,
} from "@/components/cards/cardOverlay/buttonVisibility";
import { highlightFeatureDne } from "@/lib/featureDne";
import { formatPolarPriceText, cadenceLabel, type ResolvedPrice } from "@/lib/polarPriceFormat";
import styles from "./cardPrice.module.css";

interface CardPriceProps {
  card: ContentCard;
  freeVariant?: boolean;
  /**
   * `default` — the pricing-section card as it's always been: CTA button(s),
   * whole card links to checkout.
   * `selectable` — the SAME card as a selection tile (used by the gift form):
   * no CTA/link, the whole card is a radio button; the selected card gets the
   * primary ring plus a filled check circle (ported from the app's model
   * picker, dialogModelProvider). Drive with `selected` + `onSelect`.
   */
  variant?: "default" | "selectable";
  /** Selectable variant only: whether this card is the current selection. */
  selected?: boolean;
  /** Selectable variant only: fired when the card is clicked. */
  onSelect?: () => void;
  /** Override the CMS badge: a string forces that badge text, `null` hides
   *  the badge, `undefined` (default) keeps the CMS value. */
  badgeOverride?: string | null;
}

const CardPrice = ({
  card,
  freeVariant = false,
  variant = "default",
  selected = false,
  onSelect,
  badgeOverride,
}: CardPriceProps) => {
  const selectable = variant === "selectable";
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const blocks = blocksFromCard;

  // Pricing fields now come from the CMS `price` relation on the card.
  // Keep rendering of the features list from `card.content` unchanged.
  const rawPrice = card.price as
    | { data?: { attributes?: Record<string, unknown> }; attributes?: Record<string, unknown> }
    | Record<string, unknown>
    | null
    | undefined;
  const priceAttrs: Record<string, unknown> | null =
    (rawPrice as { data?: { attributes?: Record<string, unknown> } })?.data?.attributes ??
    (rawPrice as { attributes?: Record<string, unknown> })?.attributes ??
    (rawPrice as Record<string, unknown>) ??
    null;

  const planTitle = (priceAttrs?.planTitle ?? "").toString();
  const planSubtitle = (priceAttrs?.planSubtitle ?? "").toString();
  const cmsPrice = (priceAttrs?.price ?? "").toString();
  const strikeoutPrice = (priceAttrs?.strikeoutPrice ?? "").toString();
  const planSavings = (priceAttrs?.savings ?? "").toString();
  const badgeText =
    badgeOverride !== undefined
      ? (badgeOverride ?? "").trim()
      : (priceAttrs?.badge ?? "").toString().trim();

  // Live price from Polar (attached server-side by enrichSectionsWithPolarPrices
  // when the price has a `polarProductId`). Falls back to the CMS free-text
  // price. Polar is the source of truth for what's actually charged.
  const resolvedPrice = priceAttrs?.resolvedPrice as ResolvedPrice | undefined;
  const price = resolvedPrice ? formatPolarPriceText(resolvedPrice) : cmsPrice;
  // Muted cadence label next to the price: "/mo" / "One-Time". Prefer the live
  // Polar price (precise monthly vs yearly); when Polar isn't configured on the
  // landing, fall back to the card's pricing-tab tag (`contentCard_customID`,
  // the same signal the tabs bucket on) so the cadence still shows.
  const customId = (card?.contentCard_customID ?? "").toString().toLowerCase();
  const isOneTime = /one-?time|one_time|package/.test(customId);
  const isSubscription = customId.includes("subscription");
  const cadence = resolvedPrice
    ? cadenceLabel(resolvedPrice)
    : isSubscription
      ? "/mo"
      : isOneTime
        ? "One-Time"
        : "";

  // Shared value props (CMS): a `valuePropsTitle` heading + a `valueProps`
  // markdown string. When markdown is present it replaces the card's rich-text
  // feature list; otherwise the existing `content` renders.
  const valuePropsTitle = (priceAttrs?.valuePropsTitle ?? "").toString().trim();
  const valuePropsMarkdown = (priceAttrs?.valueProps ?? "").toString().trim();

  // The price relation's `planSubtitle` is rendered as sans-serif body
  // text directly under the title/price, above the CTA. Everything in
  // the rich-text body (paragraph, heading, list) renders under the CTA.

  // Resolve buttons → which display on the card vs the overlay.
  const allCardButtons = normalizeCardButtons(card.buttons);
  const { cardButtons, overlayButtons } = splitButtons(
    allCardButtons,
    card.buttonVisibility as ButtonVisibility | undefined
  );

  // Single-CTA shortcut: when the card has exactly one button (regardless
  // of visibility — including "hidden"), the whole card surface becomes
  // that button's link. Mirrors the behavior in CardContentItem so price
  // cards behave identically to default cards.
  const wholeCardCta =
    allCardButtons.length === 1 && allCardButtons[0].href
      ? allCardButtons[0]
      : null;

  // Multi-button fallback: when there's >1 CTA and no overlay, keep the
  // existing primary-CTA Link wrap (first card-button with a URL).
  const primaryButton =
    cardButtons.find((b) => b.href) ||
    (cardButtons.length > 0 ? cardButtons[0] : null);
  const primaryHref = primaryButton?.href || "";
  const primaryIsBlank = !!primaryButton?.isBlank;

  const cardInner = (
    <Card
      className={
        selectable ? `${styles.root} ${styles.rootSelectable}` : styles.root
      }
      data-variant={freeVariant ? "free" : undefined}
      data-ph-price-card
      data-ph-plan={planTitle}
      data-ph-tab={isSubscription ? "subscription" : isOneTime ? "one-time" : "both"}
    >
      {/* Badge: centered on the card's top edge, primary color — matches the
          app paywall's "Most Popular" badge. CMS-driven text (price.badge).
          The selectable variant renders it inline beside the title instead
          (see the title row below). */}
      {badgeText && !selectable && (
        <Badge className={styles.planBadge}>
          {badgeText}
        </Badge>
      )}
      {/* Selectable variant: radio check circle on the card's left edge,
          vertically centered (the row layout comes from .rootSelectable) —
          two aria-hidden states swapped by the ternary (state is conveyed by
          aria-checked on the card button), same pattern as the app's model
          picker. */}
      {selectable &&
        (selected ? (
          <span className={styles.checkCircle} aria-hidden>
            <Check width={16} height={16} strokeWidth={2.25} absoluteStrokeWidth />
          </span>
        ) : (
          <span className={styles.checkPlaceholder} aria-hidden />
        ))}
      <CardHeader className={styles.header}>
        {/* Plan name (badge moved to the card top, above) */}
        {planTitle && (
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              {/* Selectable tiles run one type step smaller than the
                  pricing-section cards. */}
              <h3 className={`${selectable ? "title-xs" : "title-sm"} ${styles.planTitle}`}>
                {highlightFeatureDne(planTitle)}
              </h3>
              {selectable && badgeText && (
                <Badge className={styles.inlineBadge}>{badgeText}</Badge>
              )}
            </div>
          </div>
        )}

        {/* Mirrors the app paywall: the price on the left, then a column with
            the strikeout + savings badge on top and the cadence
            ("/mo" / "One-Time") below. */}
        {(price || strikeoutPrice) && (
          <div className={styles.priceRow}>
            <p className={`${selectable ? "accent-sm" : "accent-2xl"} ${styles.price}`}>
              {price}
            </p>
            <div className={styles.priceDetails}>
              {(strikeoutPrice || planSavings) && (
                <div className={styles.savingsRow}>
                  {strikeoutPrice && (
                    <p className={`body-xs ${styles.strikeoutPrice}`}>
                      {strikeoutPrice}
                    </p>
                  )}
                  {planSavings &&
                    (selectable ? (
                      // Compact rows: plain text instead of the badge chip.
                      <span className={`body-xs ${styles.savingsText}`}>
                        {planSavings}
                      </span>
                    ) : (
                      <Badge variant="secondary" className={styles.planSavingsBadge}>
                        {planSavings}
                      </Badge>
                    ))}
                </div>
              )}
              {/* Selectable tiles drop the per-card cadence — the gift form
                  says "One-time purchase" once under the card stack. */}
              {cadence && !selectable && (
                <span className={`body-xs ${styles.priceCadence}`}>{cadence}</span>
              )}
            </div>
          </div>
        )}
      </CardHeader>

      {/* The selectable variant keeps only the header (title, price, radio
          circle): subtitle, CTA, and value props are all hidden, so the
          whole content block is skipped. */}
      {!selectable && (
      <CardContent className={styles.content}>
        {/* Plan subtitle (from the price relation) sits under the
            title/price, above the CTA, muted (body-xs). The slot
            always renders — even empty — so the CTA stays aligned with
            sibling cards in a row. Min-height reserves two lines. */}
        <p className={`body-xs ${styles.subtitle}`}>
          {planSubtitle ? highlightFeatureDne(planSubtitle) : " "}
        </p>

        {/* On-card buttons. Visual <span> elements; the outer wrapper
            (Link or DialogTrigger) owns the click. Suppressed on the
            selectable variant — selection replaces the checkout CTA. */}
        {!selectable && cardButtons.length > 0 && (
          <div className={styles.buttons}>
            {cardButtons.map((button, idx) => (
              <Button
                key={button.id ?? idx}
                variant={button.variant}
                size={button.size}
                asChild
                className={styles.ctaButton}
              >
                <span title={button.title}>
                  {highlightFeatureDne(button.copy || button.title || "")}
                </span>
              </Button>
            ))}
          </div>
        )}

        {/* Feature list under the CTA. Prefer the shared CMS value props
            (a `valuePropsTitle` heading + a `valueProps` markdown string, also
            consumed by the app paywall); fall back to the card's rich-text
            body (paragraph/heading/bullet list in authored order). */}
        {valuePropsMarkdown || valuePropsTitle ? (
          <div className={styles.body}>
            {valuePropsTitle ? (
              // `title-xs` to match the free card's heading, which renders via
              // renderPriceBody (it wraps headings in `title-xs` + bodyHeading).
              <h4 className={`title-xs ${styles.bodyHeading}`}>
                {highlightFeatureDne(valuePropsTitle)}
              </h4>
            ) : null}
            {valuePropsMarkdown
              ? renderValuePropsMarkdown(valuePropsMarkdown, {
                  bulletList: styles.bullets,
                  paragraph: styles.bodyParagraph,
                })
              : null}
          </div>
        ) : (
          Array.isArray(blocks) &&
          blocks.length > 0 && (
            <div className={styles.body}>
              {renderPriceBody(blocks, {
                paragraph: styles.bodyParagraph,
                heading: styles.bodyHeading,
                bulletList: styles.bullets,
              })}
            </div>
          )
        )}
      </CardContent>
      )}
    </Card>
  );

  // 0) Selectable variant: the whole card is a radio option (the gift form
  //    wraps the grid in role="radiogroup") — no links, no overlays.
  //    `aria-checked` doubles as the CSS hook for the primary ring.
  if (selectable) {
    return (
      <button
        type="button"
        role="radio"
        className={styles.selectable}
        aria-checked={selected}
        aria-label={planTitle || "Select this option"}
        onClick={onSelect}
      >
        {cardInner}
      </button>
    );
  }

  // 1) Single CTA wins: whole card → that button's link, even when
  //    visibility is "hidden" or "all on overlay". Matches CardContentItem.
  if (wholeCardCta) {
    return (
      <Link
        href={wholeCardCta.href}
        target={wholeCardCta.isBlank ? "_blank" : undefined}
        rel={wholeCardCta.isBlank ? "noopener noreferrer" : undefined}
        title={wholeCardCta.title}
        aria-label={planTitle || wholeCardCta.copy || wholeCardCta.title}
        className={styles.clickable}
      >
        {cardInner}
      </Link>
    );
  }

  // 2) Overlay wins: multi-button card with overlay slots → whole card
  //    opens the overlay.
  if (overlayButtons.length > 0) {
    return (
      <CardOverlay
        buttons={overlayButtons}
        title={planTitle || undefined}
        trigger={
          <div
            role="button"
            tabIndex={0}
            className={styles.clickable}
            aria-label={planTitle ? `Open ${planTitle} options` : "Open options"}
          >
            {cardInner}
          </div>
        }
      />
    );
  }

  // 3) Multi-button card with no overlay → fall back to the primary CTA
  //    link wrap (first card-button with a URL).
  if (primaryButton && primaryHref) {
    return (
      <Link
        href={primaryHref}
        target={primaryIsBlank ? "_blank" : undefined}
        rel={primaryIsBlank ? "noopener noreferrer" : undefined}
        className={styles.clickable}
      >
        {cardInner}
      </Link>
    );
  }

  // 4) No buttons / no URL → non-clickable card.
  return cardInner;
};

export default CardPrice;
